import { ACCENT_COLORS } from '@scribbleplot/shared';

/**
 * Design tokens, lifted from the Scribbleplot design canvas.
 *
 * The prototype expressed these as inline literals on every element. Naming
 * them once means a palette change is a one-line edit, and it keeps the surface
 * colours consistent across screens that were drawn independently.
 */

export const color = {
  /** App background, behind everything. */
  bg: '#17181B',
  /** The canvas surface, one step darker than the chrome. */
  canvas: '#141519',
  /** Panels, menus, popovers. */
  panel: '#23252C',
  /** Cards and sidebars. */
  surface: '#1B1C21',
  surfaceAlt: '#191A1E',
  raised: '#23242A',
  raisedAlt: '#2C2F38',
  input: '#141519',

  /**
   * Text greys, lightened from the design canvas to clear WCAG AA (4.5:1) on
   * every surface here — the darkest values in the original palette measured
   * 2.3–3.8:1, which is unreadable for a lot of people.
   *
   * A 4.5:1 floor genuinely compresses the faint end of a dark theme: `dim`,
   * `faint`, and `ghost` all have to sit between 4.5 and 5.1, so `ghost` has
   * collapsed onto `faint`. It is kept as its own token because the intent
   * still differs (placeholder vs. label), and a lighter theme could separate
   * them again.
   */
  text: '#E8E9ED',
  textStrong: '#E0E2E9',
  textMuted: '#B1B5C3',
  textDim: '#A6ABBA',
  textFaint: '#9FA4B4',
  textGhost: '#9FA4B4',
  icon: '#A7AAB6',

  danger: '#F87171',
  /** Foreground on top of an accent fill. */
  onAccent: '#16121F',
  /** Ink on a sticky note. */
  ink: '#262419',

  line: 'rgba(255,255,255,.07)',
  lineStrong: 'rgba(255,255,255,.12)',
  lineSoft: 'rgba(255,255,255,.09)',
  hover: 'rgba(255,255,255,.06)',
  hoverStrong: 'rgba(255,255,255,.07)',
} as const;

export const font = {
  sans: "'Schibsted Grotesk','Helvetica Neue',Helvetica,Arial,sans-serif",
  mono: "'Space Mono',ui-monospace,SFMono-Regular,Menlo,monospace",
} as const;

export const radius = { sm: 6, md: 9, lg: 12, xl: 16, pill: 999 } as const;

export const shadow = {
  panel: '0 18px 50px rgba(0,0,0,.5)',
  float: '0 10px 30px rgba(0,0,0,.45)',
  card: '0 12px 30px rgba(0,0,0,.35)',
  sticky: '0 10px 24px rgba(0,0,0,.38)',
  toast: '0 12px 30px rgba(0,0,0,.5)',
} as const;

export const STICKY_PALETTE = ['#F6D56A', '#F49E9E', '#9BD8AC', '#8EC1F2', '#D9B8F5'] as const;

/** The accent choices live in the domain package, which also validates them. */
export const DEFAULT_ACCENT = ACCENT_COLORS[0];

/** Type ramp for text items on the canvas, in world units. */
export const TEXT_SIZES = {
  s: { fontSize: 14, fontWeight: 400, letterSpacing: 'normal' },
  m: { fontSize: 19, fontWeight: 500, letterSpacing: 'normal' },
  l: { fontSize: 33, fontWeight: 700, letterSpacing: '-.015em' },
} as const;

/** Single-letter badge per item type, used in the layers list and search. */
export const TYPE_GLYPH = {
  sticky: 'N',
  text: 'T',
  image: 'I',
  frame: 'F',
  group: 'G',
  link: 'L',
} as const;

export const CANVAS = {
  /** Dot-grid pitch in world units. */
  gridSize: 24,
  /** Below this on-screen pitch the grid reads as noise, so it is hidden. */
  gridMinScreenPitch: 6,
  minZoom: 0.06,
  maxZoom: 4,
  /** Padding when fitting content to the viewport. */
  fitPadding: 80,
  /** Pointer travel, in screen pixels, before a click becomes a drag. */
  dragThreshold: 3,
  /** Minimum size a resize gesture may produce. */
  minItemW: 50,
  minItemH: 36,
  /** Largest edge, in world units, for a newly placed image. */
  imageMaxEdge: 320,
} as const;

export const spacing = { xs: 4, sm: 8, md: 12, lg: 16, xl: 24, section: 32 } as const;
export const motion = { fast: 120, standard: 160, panel: 180 } as const;
